import { API_BASE_URL } from '../config/env';
import type { MarketplaceCategoryId } from '@claudafinil/shared';

export interface TeamMembership {
  teamId: string;
  teamName: string;
  role: 'owner' | 'member';
  joinedAt: string;
}

export interface UserInfo {
  authUserId: string;
  email: string;
  name: string;
  isPro: boolean;
  teams: TeamMembership[];
}

export interface TeamResponse {
  id: string;
  name: string;
  ownerId: string;
  inviteCode: string;
  createdAt: string;
  updatedAt: string;
  role: 'owner' | 'member';
}

export interface TeamMember {
  id: string;
  userId: string;
  email: string;
  name: string | null;
  role: 'owner' | 'member';
  joinedAt: string;
}

export interface TeamLookupResponse {
  teamId: string;
  teamName: string;
}

export interface CreateTeamInput {
  name: string;
}

export interface RenameTeamInput {
  name: string;
}

export interface CreatePromptInput {
  title: string;
  content: string;
  folderId?: string | null;
}

export interface UpdatePromptInput {
  title?: string;
  content?: string;
  folderId?: string | null;
}

export interface PromptResponse {
  id: string;
  userId: string;
  teamId: string | null;
  folderId: string | null;
  currentVersionId: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  content: string;
  editedBy: string;
  editedByName: string;
  versionCreatedAt: string;
}

export interface CloudPromptVersion {
  id: string;
  promptId: string;
  title: string;
  content: string;
  editedBy: string;
  editedByName: string;
  createdAt: string;
  isCurrent: boolean;
}

export interface PublishMarketplacePromptInput {
  sourcePromptId: string;
  categoryId: MarketplaceCategoryId;
  description: string;
  tags?: string[];
}

export interface MarketplacePromptResponse {
  id: string;
  slug: string;
  authorId: string;
  authorName: string;
  sourcePromptId: string | null;
  title: string;
  description: string;
  content: string;
  categoryId: MarketplaceCategoryId;
  tags: string[];
  saveCount: number;
  voteScore: number;
  status: 'active' | 'removed';
  createdAt: string;
  updatedAt: string;
}

export interface CreateFolderInput {
  name: string;
}

export interface UpdateFolderInput {
  name: string;
}

export interface FolderResponse {
  id: string;
  userId: string;
  teamId: string | null;
  name: string;
  createdAt: string;
}

export interface CreateAnnotationInput {
  conversationId: string;
  conversationName: string;
  messageId: string;
  messageText: string;
  platform?: 'claude' | 'chatgpt';
  note?: string;
}

export interface UpdateAnnotationInput {
  note: string;
}

export type SupportRequestKind = 'support' | 'bug' | 'feature';

export interface SupportRequestInput {
  kind: SupportRequestKind;
  message: string;
  contactEmail?: string;
  context: {
    conversationName?: string | null;
    conversationMetadata?: string;
    contractVersion?: number | null;
    adapterConfigStatus?: 'publishing' | 'applied' | 'rejected' | 'error' | null;
    adapterPlatform?: 'claude' | 'chatgpt' | null;
    adapterVersion?: string | null;
    isAuthenticated: boolean;
    authEmail?: string | null;
    authName?: string | null;
    hasAccessToken: boolean;
    appVersion: string;
    extensionVersion?: string | null;
    remoteUrl: string;
    userAgent: string;
    submittedAt: string;
  };
}

export interface AnnotationResponse {
  id: string;
  userId: string;
  teamId: string | null;
  conversationId: string;
  conversationName: string;
  messageId: string;
  messageText: string;
  platform?: 'claude' | 'chatgpt' | null;
  note: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiError {
  error: string;
  message?: string;
}

export type ApiResult<T> =
  | { success: true; data: T }
  | { success: false; error: ApiError };

export type AccessTokenGetter = () => Promise<string | null> | string | null;
export type AccessTokenRefresher = () => Promise<string | null>;

export interface ApiClientConfig {
  getAccessToken: AccessTokenGetter;
  requestAccessToken: AccessTokenRefresher;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

async function parseJsonSafely(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

function toApiError(response: Response, data: unknown): ApiError {
  if (data && typeof data === 'object' && 'error' in data) {
    const errorValue = (data as { error?: unknown }).error;
    const messageValue = (data as { message?: unknown }).message;
    return {
      error:
        typeof errorValue === 'string'
          ? errorValue
          : `HTTP ${response.status}`,
      message: typeof messageValue === 'string' ? messageValue : undefined,
    };
  }

  return {
    error: `HTTP ${response.status}`,
    message: response.statusText || undefined,
  };
}

export function createApiClient(config: ApiClientConfig) {
  const baseUrl = config.baseUrl ?? API_BASE_URL;
  const fetchImpl = config.fetchImpl ?? fetch;

  async function request<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    retryOnUnauthorized = true,
    tokenOverride?: string | null,
  ): Promise<ApiResult<T>> {
    const accessToken =
      tokenOverride === undefined
        ? await config.getAccessToken()
        : tokenOverride;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    try {
      const response = await fetchImpl(`${baseUrl}${path}`, {
        method,
        headers,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });

      if (response.status === 401 && retryOnUnauthorized) {
        const refreshedToken = await config.requestAccessToken();
        if (refreshedToken && refreshedToken !== accessToken) {
          return request<T>(method, path, body, false, refreshedToken);
        }
      }

      const data = await parseJsonSafely(response);
      if (response.status >= 400) {
        return {
          success: false,
          error: toApiError(response, data),
        };
      }

      return {
        success: true,
        data: data as T,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          error: 'Network error',
          message:
            error instanceof Error ? error.message : 'Failed to connect to API',
        },
      };
    }
  }

  async function getMe(): Promise<ApiResult<UserInfo>> {
    return request<UserInfo>('GET', '/auth/me');
  }

  async function submitSupportRequest(
    input: SupportRequestInput,
  ): Promise<ApiResult<{ success: true }>> {
    return request<{ success: true }>('POST', '/support/requests', input);
  }

  async function getPrompts(): Promise<ApiResult<{ prompts: PromptResponse[] }>> {
    return request<{ prompts: PromptResponse[] }>('GET', '/prompts');
  }

  async function createPrompt(
    input: CreatePromptInput,
  ): Promise<ApiResult<{ prompt: PromptResponse }>> {
    return request<{ prompt: PromptResponse }>('POST', '/prompts', input);
  }

  async function updatePrompt(
    id: string,
    input: UpdatePromptInput,
  ): Promise<ApiResult<{ prompt: PromptResponse }>> {
    return request<{ prompt: PromptResponse }>('PATCH', `/prompts/${id}`, input);
  }

  async function deletePrompt(id: string): Promise<ApiResult<{ success: true }>> {
    return request<{ success: true }>('DELETE', `/prompts/${id}`);
  }

  async function publishPromptToMarketplace(
    input: PublishMarketplacePromptInput,
  ): Promise<ApiResult<{ prompt: MarketplacePromptResponse }>> {
    return request<{ prompt: MarketplacePromptResponse }>('POST', '/marketplace/prompts', input);
  }

  async function getVersions(
    promptId: string,
  ): Promise<ApiResult<{ versions: CloudPromptVersion[] }>> {
    return request<{ versions: CloudPromptVersion[] }>(
      'GET',
      `/prompts/${promptId}/versions`,
    );
  }

  async function restoreVersion(
    promptId: string,
    versionId: string,
  ): Promise<ApiResult<{ prompt: PromptResponse }>> {
    return request<{ prompt: PromptResponse }>(
      'POST',
      `/prompts/${promptId}/versions/${versionId}/restore`,
    );
  }

  async function getFolders(): Promise<ApiResult<{ folders: FolderResponse[] }>> {
    return request<{ folders: FolderResponse[] }>('GET', '/folders');
  }

  async function createFolder(
    input: CreateFolderInput,
  ): Promise<ApiResult<{ folder: FolderResponse }>> {
    return request<{ folder: FolderResponse }>('POST', '/folders', input);
  }

  async function renameFolder(
    id: string,
    input: UpdateFolderInput,
  ): Promise<ApiResult<{ folder: FolderResponse }>> {
    return request<{ folder: FolderResponse }>('PATCH', `/folders/${id}`, input);
  }

  async function deleteFolder(id: string): Promise<ApiResult<{ success: true }>> {
    return request<{ success: true }>('DELETE', `/folders/${id}`);
  }

  async function getAnnotations(): Promise<ApiResult<{ annotations: AnnotationResponse[] }>> {
    return request<{ annotations: AnnotationResponse[] }>('GET', '/annotations');
  }

  async function createAnnotation(
    input: CreateAnnotationInput,
  ): Promise<ApiResult<{ annotation: AnnotationResponse }>> {
    return request<{ annotation: AnnotationResponse }>('POST', '/annotations', input);
  }

  async function updateAnnotation(
    id: string,
    input: UpdateAnnotationInput,
  ): Promise<ApiResult<{ annotation: AnnotationResponse }>> {
    return request<{ annotation: AnnotationResponse }>('PATCH', `/annotations/${id}`, input);
  }

  async function deleteAnnotation(
    id: string,
  ): Promise<ApiResult<{ success: true }>> {
    return request<{ success: true }>('DELETE', `/annotations/${id}`);
  }

  async function getTeams(): Promise<ApiResult<{ teams: TeamResponse[] }>> {
    return request<{ teams: TeamResponse[] }>('GET', '/teams');
  }

  async function createTeam(
    input: CreateTeamInput,
  ): Promise<ApiResult<{ team: TeamResponse }>> {
    return request<{ team: TeamResponse }>('POST', '/teams', input);
  }

  async function renameTeam(
    teamId: string,
    input: RenameTeamInput,
  ): Promise<ApiResult<{ team: TeamResponse }>> {
    return request<{ team: TeamResponse }>('PATCH', `/teams/${teamId}`, input);
  }

  async function deleteTeam(teamId: string): Promise<ApiResult<{ success: true }>> {
    return request<{ success: true }>('DELETE', `/teams/${teamId}`);
  }

  async function getTeamMembers(
    teamId: string,
  ): Promise<ApiResult<{ members: TeamMember[] }>> {
    return request<{ members: TeamMember[] }>('GET', `/teams/${teamId}/members`);
  }

  async function removeTeamMember(
    teamId: string,
    userId: string,
  ): Promise<ApiResult<{ success: true }>> {
    return request<{ success: true }>(
      'DELETE',
      `/teams/${teamId}/members/${userId}`,
    );
  }

  async function leaveTeam(teamId: string): Promise<ApiResult<{ success: true }>> {
    return request<{ success: true }>('POST', `/teams/${teamId}/leave`);
  }

  async function regenerateInviteCode(
    teamId: string,
  ): Promise<ApiResult<{ inviteCode: string }>> {
    return request<{ inviteCode: string }>('POST', `/teams/${teamId}/invite-code`);
  }

  async function lookupTeamByInviteCode(
    inviteCode: string,
  ): Promise<ApiResult<TeamLookupResponse>> {
    return request<TeamLookupResponse>('GET', `/teams/invite/${inviteCode}`);
  }

  async function joinTeam(
    inviteCode: string,
  ): Promise<ApiResult<{ team: TeamResponse }>> {
    return request<{ team: TeamResponse }>('POST', `/teams/join/${inviteCode}`);
  }

  async function getTeamPrompts(
    teamId: string,
  ): Promise<ApiResult<{ prompts: PromptResponse[] }>> {
    return request<{ prompts: PromptResponse[] }>('GET', `/teams/${teamId}/prompts`);
  }

  async function createTeamPrompt(
    teamId: string,
    input: CreatePromptInput,
  ): Promise<ApiResult<{ prompt: PromptResponse }>> {
    return request<{ prompt: PromptResponse }>(
      'POST',
      `/teams/${teamId}/prompts`,
      input,
    );
  }

  async function updateTeamPrompt(
    teamId: string,
    promptId: string,
    input: UpdatePromptInput,
  ): Promise<ApiResult<{ prompt: PromptResponse }>> {
    return request<{ prompt: PromptResponse }>(
      'PATCH',
      `/teams/${teamId}/prompts/${promptId}`,
      input,
    );
  }

  async function deleteTeamPrompt(
    teamId: string,
    promptId: string,
  ): Promise<ApiResult<{ success: true }>> {
    return request<{ success: true }>(
      'DELETE',
      `/teams/${teamId}/prompts/${promptId}`,
    );
  }

  async function getTeamPromptVersions(
    teamId: string,
    promptId: string,
  ): Promise<ApiResult<{ versions: CloudPromptVersion[] }>> {
    return request<{ versions: CloudPromptVersion[] }>(
      'GET',
      `/teams/${teamId}/prompts/${promptId}/versions`,
    );
  }

  async function restoreTeamPromptVersion(
    teamId: string,
    promptId: string,
    versionId: string,
  ): Promise<ApiResult<{ prompt: PromptResponse }>> {
    return request<{ prompt: PromptResponse }>(
      'POST',
      `/teams/${teamId}/prompts/${promptId}/versions/${versionId}/restore`,
    );
  }

  async function getTeamFolders(
    teamId: string,
  ): Promise<ApiResult<{ folders: FolderResponse[] }>> {
    return request<{ folders: FolderResponse[] }>('GET', `/teams/${teamId}/folders`);
  }

  async function createTeamFolder(
    teamId: string,
    input: CreateFolderInput,
  ): Promise<ApiResult<{ folder: FolderResponse }>> {
    return request<{ folder: FolderResponse }>(
      'POST',
      `/teams/${teamId}/folders`,
      input,
    );
  }

  async function renameTeamFolder(
    teamId: string,
    folderId: string,
    input: UpdateFolderInput,
  ): Promise<ApiResult<{ folder: FolderResponse }>> {
    return request<{ folder: FolderResponse }>(
      'PATCH',
      `/teams/${teamId}/folders/${folderId}`,
      input,
    );
  }

  async function deleteTeamFolder(
    teamId: string,
    folderId: string,
  ): Promise<ApiResult<{ success: true }>> {
    return request<{ success: true }>(
      'DELETE',
      `/teams/${teamId}/folders/${folderId}`,
    );
  }

  async function getTeamAnnotations(
    teamId: string,
  ): Promise<ApiResult<{ annotations: AnnotationResponse[] }>> {
    return request<{ annotations: AnnotationResponse[] }>(
      'GET',
      `/teams/${teamId}/annotations`,
    );
  }

  async function createTeamAnnotation(
    teamId: string,
    input: CreateAnnotationInput,
  ): Promise<ApiResult<{ annotation: AnnotationResponse }>> {
    return request<{ annotation: AnnotationResponse }>(
      'POST',
      `/teams/${teamId}/annotations`,
      input,
    );
  }

  async function updateTeamAnnotation(
    teamId: string,
    annotationId: string,
    input: UpdateAnnotationInput,
  ): Promise<ApiResult<{ annotation: AnnotationResponse }>> {
    return request<{ annotation: AnnotationResponse }>(
      'PATCH',
      `/teams/${teamId}/annotations/${annotationId}`,
      input,
    );
  }

  async function deleteTeamAnnotation(
    teamId: string,
    annotationId: string,
  ): Promise<ApiResult<{ success: true }>> {
    return request<{ success: true }>(
      'DELETE',
      `/teams/${teamId}/annotations/${annotationId}`,
    );
  }

  return {
    getMe,
    submitSupportRequest,
    getPrompts,
    createPrompt,
    updatePrompt,
    deletePrompt,
    publishPromptToMarketplace,
    getVersions,
    restoreVersion,
    getFolders,
    createFolder,
    renameFolder,
    deleteFolder,
    getAnnotations,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
    getTeams,
    createTeam,
    renameTeam,
    deleteTeam,
    getTeamMembers,
    removeTeamMember,
    leaveTeam,
    regenerateInviteCode,
    lookupTeamByInviteCode,
    joinTeam,
    getTeamPrompts,
    createTeamPrompt,
    updateTeamPrompt,
    deleteTeamPrompt,
    getTeamPromptVersions,
    restoreTeamPromptVersion,
    getTeamFolders,
    createTeamFolder,
    renameTeamFolder,
    deleteTeamFolder,
    getTeamAnnotations,
    createTeamAnnotation,
    updateTeamAnnotation,
    deleteTeamAnnotation,
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
